import {Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, inject} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DialogService} from "../../../shared/services/dialog.service";
import {DocumentService} from "../../../shared/services/document.service";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {DatePipe, NgOptimizedImage, NgStyle} from "@angular/common";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {VideoSrcDirective} from "../../../shared/directives/video-src.directive";
import {ReactionPickerComponent,ReactionSelection} from '../../../shared/elements/reaction-picker/reaction-picker.component';
import {EmojiPillComponent} from "../../../shared/elements/emoji-pill/emoji-pill.component";
import {EmojiPillItem} from "../../../shared/elements/emoji-pill/emoji-pill.types";
import {ChatStore} from '../../chat/chat-store/chat-store';
import {ChatMessageResource} from "../../../shared/types/generated/chatMessageResource";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {Dialog} from "@syncfusion/ej2-angular-popups";
import {LanguageService} from "../../../shared/services/language.service";
import {DialogChatReadsComponent} from "../../chat/dialog-chat-reads/dialog-chat-reads.component";
import {FileSizePipe} from "../../../shared/pipes/file-size.pipe";
import {FileStorageResource} from '../../../shared/types/generated/fileStorageResource';
import {NgSrcImageErrorDirective} from '../../../shared/directives/ng-src-image-error.directive';
import {VideoSourceErrorDirective} from '../../../shared/directives/video-source-error.directive';

@Component({
    selector: 'app-dialog-documents-gallery',
    imports: [
        DialogModule,
        FormButtonComponent,
        HeadlineComponent,
        TooltipModule,
        TranslatePipe,
        IconBadgeComponent,
        DatePipe,
        FileSizePipe,
        NgOptimizedImage,
        TooltipComponent,
        NgStyle,
        FormLabelComponent,
        VideoSrcDirective,
        ReactionPickerComponent,
        EmojiPillComponent,
        DialogChatReadsComponent,
        NgSrcImageErrorDirective,
        VideoSourceErrorDirective
    ],
    templateUrl: './dialog-documents-gallery.component.html',
    styleUrl: './dialog-documents-gallery.component.scss'
})
export class DialogDocumentsGalleryComponent {
  public dialogService: DialogService = inject(DialogService);
  public documentService: DocumentService = inject(DocumentService);
  private chatStore: InstanceType<typeof ChatStore> = inject(ChatStore);
  private languageService: LanguageService = inject(LanguageService);

  @ViewChild('imagePreviewElement', {static: false}) public imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild('mobileMenuToggle', {static: false}) public mobileMenuToggle?: ElementRef<HTMLButtonElement>;
  @HostListener('document:mousemove', ['$event']) public onMouseMove(event: MouseEvent): void {
    if (this.isZoomed) {
      this.updatePosition(event);
    }
  }

  @Input() public visible: boolean = false;
  @Input() public hideReactions: boolean = false;
  @Input() public showChatOptions: boolean = false;
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public linkDialog: boolean = false;
  public infoDialog: boolean = false;
  public isZoomed: boolean = false;
  public translateX: number = 0;
  public translateY: number = 0;
  public scale: number = 1;
  public passwordVisible: boolean = false;
  public chatReadsDialog: boolean = false;
  private dialogInstance: Dialog | null = null;

  public currentGalleryMessageId(): number {
    const currentFile = this.documentService.documentsGalleryFiles[this.documentService.documentsGalleryPosition];
    const fileId = currentFile?.uuid;

    if (fileId && this.documentService.documentsGalleryFileToMessageMap.has(fileId)) {
      return this.documentService.documentsGalleryFileToMessageMap.get(fileId)!;
    }

    return this.documentService.documentsGalleryMessageId;
  }

  public currentGalleryFile(): FileStorageResource {
    return this.documentService.documentsGalleryFiles[this.documentService.documentsGalleryPosition];
  }

  public downloadCurrentFile(): void {
    this.documentService.downloadDocument(this.currentGalleryFile());
  }

  public currentGalleryMessage(): ChatMessageResource | undefined {
    const messageId = this.currentGalleryMessageId();
    if (messageId <= 0) {
      return undefined;
    }

    return this.chatStore.activeChatMessages().find((message) => message.id === messageId);
  }

  public reactionEntries(): EmojiPillItem[] {
    const message = this.currentGalleryMessage();
    if (!message?.emojis?.length) {
      return [];
    }

    return message.emojis.map((emojiResource) => ({
      referenceId: message.id,
      emoji: emojiResource.emoji,
      userId: emojiResource.user.id,
      userName: `${emojiResource.user.first_name} ${emojiResource.user.last_name}`
    }));
  }

  public currentReactionEmoji(): string | null {
    return this.reactionEntries()[0]?.emoji ?? null;
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public isCurrentMessageFavorite(): boolean {
    return !!this.currentGalleryMessage()?.favorite;
  }

  public isGroupChatContext(): boolean {
    return (this.chatStore.activeChatDetails()?.users?.length ?? 0) > 2;
  }

  public openReadsDialog(): void {
    this.closeMobileDropdown();
    if (this.currentGalleryMessageId() > 0) {
      this.chatReadsDialog = true;
    }
  }

  public openMobileReactionsOverview(emojiPill: EmojiPillComponent): void {
    this.closeMobileDropdown();
    emojiPill.openDialog();
  }

  public reply(): void {
    const messageId = this.currentGalleryMessageId();
    if (messageId <= 0) {
      return;
    }

    this.chatStore.setReplyMessage(messageId);
    this.close();
  }

  public star(): void {
    const message = this.currentGalleryMessage();
    if (!message) {
      return;
    }

    this.chatStore.setChatMessageFavorite(message.id, !message.favorite);
  }

  public keep(): void {
    const message = this.currentGalleryMessage();
    if (!message) {
      return;
    }

    this.chatStore.pinChatMessage(message.id, !message.pinned);
  }

  public forward(): void {
    const messageId = this.currentGalleryMessageId();
    if (messageId <= 0) {
      return;
    }

    this.chatStore.resetChatMessageSelection();
    if (!this.chatStore.isMessageSelected(messageId)) {
      this.chatStore.toggleSelectChatMessage(messageId);
    }
    this.documentService.documentsGalleryForwardDialog = true;
    this.close();
  }

  public delete(): void {
    const message = this.currentGalleryMessage();
    if (!message) {
      return;
    }

    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-muted fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('CONFIRMITEMDELETE') + '</h6>',
      content: this.languageService.getTranslationByKey('CHATDELETEONLYYOURSORALLMESSAGES'),
      cancelButton: {
        text: this.languageService.getTranslationByKey('CHATDELETEMINE'),
        click: () => this.deleteConfirmed('mine', message),
        cssClass: 'bg-transparent'
      },
      okButton: {
        text: this.languageService.getTranslationByKey('CHATDELETEALL'),
        click: () => this.deleteConfirmed('all', message)
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  private deleteConfirmed(type: 'mine' | 'all', message: ChatMessageResource): void {
    if (type === 'all' && message.editable) {
      this.chatStore.removeMessageForAll([message.id]);
    } else {
      this.chatStore.removeMessageForMe([message.id]);
    }

    this.dialogInstance?.hide();
    this.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public updateLink(): void { }

  public step(direction: 'forward' | 'back'): void {
    if (direction === 'forward') {
      this.documentService.documentsGalleryPosition++;
    } else {
      this.documentService.documentsGalleryPosition--;
    }
  }

  public imageClicked(event: MouseEvent): void {
    this.isZoomed = !this.isZoomed;

    if (this.isZoomed) {
      this.scale = 2;
      this.updatePosition(event);
    } else {
      this.resetZoom();
    }
  }

  private updatePosition(event: MouseEvent): void {
    const rect = this.imageElement.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    const percentX = (offsetX / rect.width - 0.5) * 2;
    const percentY = (offsetY / rect.height - 0.5) * 2;

    this.translateX = -percentX * (rect.width / 4);
    this.translateY = -percentY * (rect.height / 4);
  }

  private resetZoom(): void {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
  }

  public onReactionSelection(sel: ReactionSelection): void {
    const messageId = this.currentGalleryMessageId();

    if (messageId > 0) {
      this.chatStore.setChatMessageReaction(messageId, sel.emoji);
    }
  }

  public onEmojiPillDelete(referenceId: number): void {
    if (referenceId > 0) {
      this.chatStore.setChatMessageReaction(referenceId, '');
    }
  }

  private closeMobileDropdown(): void {
    const toggleButton = this.mobileMenuToggle?.nativeElement;
    if (toggleButton?.getAttribute('aria-expanded') === 'true') {
      toggleButton.click();
    }
  }
}
